import React, { useEffect } from "react";
import { Button } from "src/business-types/grocery/components/Button";
import { useConfigurationState } from "./hooks/useConfigurationState";
import { DEFAULT_SHIFT_MANAGE } from "./types/configuration.types";
import { PageTitle } from "../PageTitle";

const BusinessOperationTab = () => {
  const {
    user,
    businessOperationEnable,
    setBusinessOperationEnable,
    businessOperationType,
    setBusinessOperationType,
    shiftTypes,
    newShift,
    setNewShift,
    addShift,
    removeShift,
    printProductLimit,
    setPrintProductLimit,
    saveBusinessOperation,
  } = useConfigurationState();

  const handleSave = () => {
    saveBusinessOperation({
      businessOperationEnable,
      businessOperationType,
      shiftTypes,
    });
  };

  useEffect(() => {
    if (user?.business_type !== "Grocery") {
      document.title = "Business Operation › Configuration › iMetanic";
    }
  }, []);

  return (
    <div className='w-full min-h-screen bg-gray-50'>
      <PageTitle title=' - Configuration ›  Business Operation' />{" "}
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6'>
        {/* Only render for Restaurant */}
        {user?.business_type !== "Restaurant" ? (
          <div className='w-full border p-8 rounded-xl shadow bg-white text-center'>
            <p className='text-gray-500'>Business Operation settings are only available for Restaurant business type.</p>
          </div>
        ) : (
          <div className='w-full border p-6 rounded-xl shadow bg-white'>
            <h2 className='text-xl font-bold mb-6'>Business Operation Configuration</h2>

            {/* Enable Toggle */}
            <div className='flex items-center gap-3 mb-6'>
              <label className='flex items-center gap-2 cursor-pointer'>
                <input
                  type='checkbox'
                  checked={businessOperationEnable}
                  onChange={() => setBusinessOperationEnable(!businessOperationEnable)}
                  className='w-4 h-4'
                />
                <span className='font-medium'>Enable Business Operation</span>
              </label>
            </div>

            {!businessOperationEnable && (
              <p className='text-sm text-gray-500 mb-6 bg-gray-50 p-3 rounded-lg'>Enable this to allow Day or Shift open/close operations.</p>
            )}

            {businessOperationEnable && (
              <>
                {/* Operation Type */}
                <div className='mb-6'>
                  <label className='block text-sm font-semibold text-gray-700 mb-3'>Operation Type</label>
                  <div className='flex gap-6'>
                    <label className='flex items-center gap-2'>
                      <input
                        type='radio'
                        value='Day'
                        checked={businessOperationType === "Day"}
                        onChange={() => setBusinessOperationType("Day")}
                        className='w-4 h-4'
                      />
                      <span>Day</span>
                    </label>
                    <label className='flex items-center gap-2'>
                      <input
                        type='radio'
                        value='Shift'
                        checked={businessOperationType === "Shift"}
                        onChange={() => setBusinessOperationType("Shift")}
                        className='w-4 h-4'
                      />
                      <span>Shift</span>
                    </label>
                  </div>
                </div>

                {/* Print Product Limit */}
                <div className='mb-6'>
                  <label className='block text-sm font-semibold text-gray-700 mb-3'>Maximum Products in Print</label>
                  <select
                    value={printProductLimit}
                    onChange={(e) => setPrintProductLimit(e.target.value)}
                    className='w-full md:w-64 h-11 rounded-lg border border-gray-300 bg-white px-4 text-sm
                      focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition'
                  >
                    <option value='5'>5 Products</option>
                    <option value='10'>10 Products</option>
                    <option value='20'>20 Products</option>
                    <option value='50'>50 Products</option>
                    <option value='100'>100 Products</option>
                    <option value='all'>Show All</option>
                  </select>
                  <p className='text-xs text-gray-500 mt-2'>Choose how many products should appear in printed bills.</p>
                </div>

                {/* Shift Management */}
                {businessOperationType === "Shift" && (
                  <div className='mt-6 p-4 bg-gray-50 rounded-lg'>
                    <h3 className='text-md font-semibold mb-3'>Manage Shifts</h3>

                    {/* Shift List */}
                    <div className='flex flex-wrap gap-2 mb-4'>
                      {shiftTypes.map((shift, index) => (
                        <div key={index} className='flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm'>
                          <span>{shift}</span>
                          <button onClick={() => removeShift(shift)} className='text-red-600 hover:text-red-800 font-bold text-lg'>
                            ×
                          </button>
                        </div>
                      ))}
                    </div>

                    {/* Add New Shift */}
                    {shiftTypes.length < 5 && (
                      <div className='flex gap-2'>
                        <input
                          type='text'
                          value={newShift}
                          onChange={(e) => setNewShift(e.target.value)}
                          placeholder='Enter shift name'
                          className='border px-3 py-2 rounded-md text-sm flex-1 max-w-xs'
                          onKeyPress={(e) => e.key === "Enter" && addShift()}
                        />
                        <button onClick={addShift} className='bg-blue-600 text-white px-4 py-2 rounded-md text-sm hover:bg-blue-700'>
                          Add Shift
                        </button>
                      </div>
                    )}

                    {shiftTypes.length >= 5 && <p className='text-xs text-red-500 mt-2'>Maximum 5 shifts allowed</p>}

                    <p className='text-xs text-gray-500 mt-3'>Default shifts: {DEFAULT_SHIFT_MANAGE.join(", ")}</p>
                  </div>
                )}
              </>
            )}

            {/* Save Button */}
            <div className='flex justify-center mt-6'>
              <Button label='Save Business Operation Settings' onClick={handleSave} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessOperationTab;
